import { useCallback, useState } from 'react'
import type { ProjectDocument } from '@/lib/db/rxdb/schemas/project'
import type { SpaceOrderDocument } from '@/lib/db/rxdb/hooks'
import { useSpaceOrderMutations } from '@/lib/db/rxdb/hooks'
import { compareRanks, generateRankBetween } from '@/lib/utils/lexorank'
import { sortSpacesByPrivateOrder } from '@/lib/utils/spaceOrder'
import { rebalanceSortOrders } from '@/lib/utils/sortOrder'

export type SpaceDropPosition = 'before' | 'after'

export type SpaceReorderEvent = {
  sourceId: string
  targetId: string
  dropPosition: SpaceDropPosition
}

/**
 * Reorder root Spaces in the sidebar. Unlike useProjectReorder — which writes the
 * shared Project.sortOrder that every member sees — this writes private
 * per-user rank rows, so dragging a Space never moves it for a teammate.
 *
 * `spaces` must already be in display order (see sortSpacesByPrivateOrder).
 */
export function useSpaceReorder(
  spaces: Array<ProjectDocument>,
  spaceOrders: Array<SpaceOrderDocument>,
) {
  const [draggingId, setDraggingId] = useState<string | null>(null)
  const [dropIndicator, setDropIndicator] = useState<{
    id: string
    position: SpaceDropPosition
  } | null>(null)

  const { setSpaceRank } = useSpaceOrderMutations()

  const handleReorder = useCallback(
    (event: SpaceReorderEvent) => {
      if (event.sourceId === event.targetId) return

      const ordered = sortSpacesByPrivateOrder(
        spaces.filter((space) => !space.isDeleted),
        spaceOrders
          .filter((order) => !order.isDeleted)
          .map((order) => ({ projectId: order.projectId, sortOrder: order.sortOrder })),
      )

      const source = ordered.find((space) => space.id === event.sourceId)
      if (!source) return

      const reordered = ordered.filter((space) => space.id !== event.sourceId)
      const targetIndex = reordered.findIndex((space) => space.id === event.targetId)
      if (targetIndex === -1) return

      const insertIndex =
        event.dropPosition === 'after' ? targetIndex + 1 : targetIndex
      reordered.splice(insertIndex, 0, source)

      const rankByProjectId = new Map(
        spaceOrders
          .filter((order) => !order.isDeleted)
          .map((order) => [order.projectId, order.sortOrder]),
      )

      const movedIndex = reordered.findIndex((space) => space.id === source.id)
      const beforeRank =
        movedIndex > 0
          ? (rankByProjectId.get(reordered[movedIndex - 1]!.id) ?? null)
          : null
      const afterRank =
        movedIndex < reordered.length - 1
          ? (rankByProjectId.get(reordered[movedIndex + 1]!.id) ?? null)
          : null

      // Lazy materialisation: until the first drag a user has no rows at all, so
      // there are no neighbouring ranks to interpolate between. Write a rank for
      // every visible Space in the new order, which pins the whole arrangement in
      // one pass. The same path covers a collapsed rank gap.
      const hasUnrankedSpaces = reordered.some(
        (space) => !rankByProjectId.get(space.id),
      )
      const shouldMaterializeAll =
        hasUnrankedSpaces ||
        (beforeRank !== null &&
          afterRank !== null &&
          compareRanks(beforeRank, afterRank) >= 0)

      if (shouldMaterializeAll) {
        void rebalanceSortOrders(
          reordered.map((space) => ({
            id: space.id,
            sortOrder: rankByProjectId.get(space.id) ?? null,
          })),
          async (projectId, updates) => {
            await setSpaceRank(projectId, updates.sortOrder)
          },
        )
        return
      }

      const nextSortOrder = generateRankBetween(beforeRank, afterRank)
      if (nextSortOrder === rankByProjectId.get(source.id)) return

      void setSpaceRank(source.id, nextSortOrder)
    },
    [spaces, spaceOrders, setSpaceRank],
  )

  return {
    handleReorder,
    draggingId,
    setDraggingId,
    dropIndicator,
    setDropIndicator,
  }
}
